/* eslint-disable */
import Long from "long";
import _m0 from "protobufjs/minimal";
import { Coin } from 'cosmjs-types/cosmos/base/v1beta1/coin';
import { Duration } from "cosmjs-types/google/protobuf/duration";
import { Timestamp } from "cosmjs-types/google/protobuf/timestamp";

export const protobufPackage = "dymensionxyz.dymension.iro";

/** Params is a module parameters. */
export interface Params {
  takerFee: string;
  creationFee: string;
  /**
   * Minimum plan duration
   * when creating a plan, the plan duration should be greater than or equal to
   * this value plan duration is from the start time to the pre-launch time
   */
  minPlanDuration:
    | Duration
    | undefined;
  /**
   * The minimum time after settlement when the incentives distribution will
   * start
   */
  incentivesMinStartTimeAfterSettlement:
    | Duration
    | undefined;
  /** The minimum number of epochs over which the incentives will be paid */
  incentivesMinNumEpochsPaidOver: number;
}

/**
 * Bonding curve represents a bonding curve in the IRO module.
 * BondingCurve represents a bonding curve with parameters M, N, and C.
 * The price of the token is calculated as follows:
 * price = M * x^N + C
 */
export interface BondingCurve {
  M: string;
  N: string;
  C: string;
}

/** Plan represents a plan in the IRO module. */
export interface Plan {
  /** The ID of the plan. */
  id: number;
  /** The ID of the rollapp. */
  rollappId: string;
  /** The module account address to hold the raised DYM tokens. */
  moduleAccAddress: string;
  /** The total amount of tokens allocated for the IRO. */
  totalAllocation: Coin | undefined;
  bondingCurve:
    | BondingCurve
    | undefined;
  /**
   * If set, the plan is settled, and the minted allocated tokens can be claimed
   * for this settled_denom
   */
  settledDenom: string;
  /** The start time of the plan. */
  startTime:
    | Date
    | undefined;
  /** The time before which the rollapp cannot be started. */
  preLaunchTime:
    | Date
    | undefined;
  /** The amount of tokens sold so far. */
  soldAmt: string;
  /** The amount of tokens claimed so far. */
  claimedAmt: string;
  /**
   * The incentive plan parameters for the tokens left after the plan is
   * settled.
   */
  incentivePlanParams: IncentivePlanParams | undefined;
}

export interface IncentivePlanParams {
  /**
   * start_time_after_settlement is the time after IRO settlement when the
   * distribution of the remaining tokens as incentives will start
   */
  startTimeAfterSettlement:
    | Duration
    | undefined;
  /**
   * num_epochs_paid_over is the number of total epochs distribution will be
   * completed over
   */
  numEpochsPaidOver: number;
}

function createBaseParams(): Params {
  return {
    takerFee: "",
    creationFee: "",
    minPlanDuration: undefined,
    incentivesMinStartTimeAfterSettlement: undefined,
    incentivesMinNumEpochsPaidOver: 0,
  };
}

export const Params = {
  encode(message: Params, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.takerFee !== "") {
      writer.uint32(10).string(message.takerFee);
    }
    if (message.creationFee !== "") {
      writer.uint32(18).string(message.creationFee);
    }
    if (message.minPlanDuration !== undefined) {
      Duration.encode(message.minPlanDuration, writer.uint32(26).fork()).ldelim();
    }
    if (message.incentivesMinStartTimeAfterSettlement !== undefined) {
      Duration.encode(message.incentivesMinStartTimeAfterSettlement, writer.uint32(34).fork()).ldelim();
    }
    if (message.incentivesMinNumEpochsPaidOver !== 0) {
      writer.uint32(40).uint64(message.incentivesMinNumEpochsPaidOver);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Params {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseParams();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.takerFee = reader.string();
          break;
        case 2:
          message.creationFee = reader.string();
          break;
        case 3:
          message.minPlanDuration = Duration.decode(reader, reader.uint32());
          break;
        case 4:
          message.incentivesMinStartTimeAfterSettlement = Duration.decode(reader, reader.uint32());
          break;
        case 5:
          message.incentivesMinNumEpochsPaidOver = longToNumber(reader.uint64() as Long);
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Params {
    return {
      takerFee: isSet(object.takerFee) ? String(object.takerFee) : "",
      creationFee: isSet(object.creationFee) ? String(object.creationFee) : "",
      minPlanDuration: isSet(object.minPlanDuration) ? Duration.fromJSON(object.minPlanDuration) : undefined,
      incentivesMinStartTimeAfterSettlement: isSet(object.incentivesMinStartTimeAfterSettlement)
        ? Duration.fromJSON(object.incentivesMinStartTimeAfterSettlement)
        : undefined,
      incentivesMinNumEpochsPaidOver: isSet(object.incentivesMinNumEpochsPaidOver)
        ? Number(object.incentivesMinNumEpochsPaidOver)
        : 0,
    };
  },

  toJSON(message: Params): unknown {
    const obj: any = {};
    message.takerFee !== undefined && (obj.takerFee = message.takerFee);
    message.creationFee !== undefined && (obj.creationFee = message.creationFee);
    message.minPlanDuration !== undefined
      && (obj.minPlanDuration = message.minPlanDuration ? Duration.toJSON(message.minPlanDuration) : undefined);
    message.incentivesMinStartTimeAfterSettlement !== undefined
      && (obj.incentivesMinStartTimeAfterSettlement = message.incentivesMinStartTimeAfterSettlement
        ? Duration.toJSON(message.incentivesMinStartTimeAfterSettlement)
        : undefined);
    message.incentivesMinNumEpochsPaidOver !== undefined
      && (obj.incentivesMinNumEpochsPaidOver = Math.round(message.incentivesMinNumEpochsPaidOver));
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<Params>, I>>(object: I): Params {
    const message = createBaseParams();
    message.takerFee = object.takerFee ?? "";
    message.creationFee = object.creationFee ?? "";
    message.minPlanDuration = (object.minPlanDuration !== undefined && object.minPlanDuration !== null)
      ? Duration.fromPartial(object.minPlanDuration as Duration)
      : undefined;
    message.incentivesMinStartTimeAfterSettlement =
      (object.incentivesMinStartTimeAfterSettlement !== undefined
          && object.incentivesMinStartTimeAfterSettlement !== null)
        ? Duration.fromPartial(object.incentivesMinStartTimeAfterSettlement as Duration)
        : undefined;
    message.incentivesMinNumEpochsPaidOver = object.incentivesMinNumEpochsPaidOver ?? 0;
    return message;
  },
};

function createBaseBondingCurve(): BondingCurve {
  return { M: "", N: "", C: "" };
}

export const BondingCurve = {
  encode(message: BondingCurve, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.M !== "") {
      writer.uint32(10).string(message.M);
    }
    if (message.N !== "") {
      writer.uint32(18).string(message.N);
    }
    if (message.C !== "") {
      writer.uint32(26).string(message.C);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): BondingCurve {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseBondingCurve();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.M = reader.string();
          break;
        case 2:
          message.N = reader.string();
          break;
        case 3:
          message.C = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): BondingCurve {
    return {
      M: isSet(object.M) ? String(object.M) : "",
      N: isSet(object.N) ? String(object.N) : "",
      C: isSet(object.C) ? String(object.C) : "",
    };
  },

  toJSON(message: BondingCurve): unknown {
    const obj: any = {};
    message.M !== undefined && (obj.M = message.M);
    message.N !== undefined && (obj.N = message.N);
    message.C !== undefined && (obj.C = message.C);
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<BondingCurve>, I>>(object: I): BondingCurve {
    const message = createBaseBondingCurve();
    message.M = object.M ?? "";
    message.N = object.N ?? "";
    message.C = object.C ?? "";
    return message;
  },
};

function createBasePlan(): Plan {
  return {
    id: 0,
    rollappId: "",
    moduleAccAddress: "",
    totalAllocation: undefined,
    bondingCurve: undefined,
    settledDenom: "",
    startTime: undefined,
    preLaunchTime: undefined,
    soldAmt: "",
    claimedAmt: "",
    incentivePlanParams: undefined,
  };
}

export const Plan = {
  encode(message: Plan, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.id !== 0) {
      writer.uint32(8).uint64(message.id);
    }
    if (message.rollappId !== "") {
      writer.uint32(18).string(message.rollappId);
    }
    if (message.moduleAccAddress !== "") {
      writer.uint32(26).string(message.moduleAccAddress);
    }
    if (message.totalAllocation !== undefined) {
      Coin.encode(message.totalAllocation, writer.uint32(34).fork()).ldelim();
    }
    if (message.bondingCurve !== undefined) {
      BondingCurve.encode(message.bondingCurve, writer.uint32(42).fork()).ldelim();
    }
    if (message.settledDenom !== "") {
      writer.uint32(50).string(message.settledDenom);
    }
    if (message.startTime !== undefined) {
      Timestamp.encode(toTimestamp(message.startTime), writer.uint32(58).fork()).ldelim();
    }
    if (message.preLaunchTime !== undefined) {
      Timestamp.encode(toTimestamp(message.preLaunchTime), writer.uint32(66).fork()).ldelim();
    }
    if (message.soldAmt !== "") {
      writer.uint32(74).string(message.soldAmt);
    }
    if (message.claimedAmt !== "") {
      writer.uint32(82).string(message.claimedAmt);
    }
    if (message.incentivePlanParams !== undefined) {
      IncentivePlanParams.encode(message.incentivePlanParams, writer.uint32(90).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Plan {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePlan();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.id = longToNumber(reader.uint64() as Long);
          break;
        case 2:
          message.rollappId = reader.string();
          break;
        case 3:
          message.moduleAccAddress = reader.string();
          break;
        case 4:
          message.totalAllocation = Coin.decode(reader, reader.uint32());
          break;
        case 5:
          message.bondingCurve = BondingCurve.decode(reader, reader.uint32());
          break;
        case 6:
          message.settledDenom = reader.string();
          break;
        case 7:
          message.startTime = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
          break;
        case 8:
          message.preLaunchTime = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
          break;
        case 9:
          message.soldAmt = reader.string();
          break;
        case 10:
          message.claimedAmt = reader.string();
          break;
        case 11:
          message.incentivePlanParams = IncentivePlanParams.decode(reader, reader.uint32());
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Plan {
    return {
      id: isSet(object.id) ? Number(object.id) : 0,
      rollappId: isSet(object.rollappId) ? String(object.rollappId) : "",
      moduleAccAddress: isSet(object.moduleAccAddress) ? String(object.moduleAccAddress) : "",
      totalAllocation: isSet(object.totalAllocation) ? Coin.fromJSON(object.totalAllocation) : undefined,
      bondingCurve: isSet(object.bondingCurve) ? BondingCurve.fromJSON(object.bondingCurve) : undefined,
      settledDenom: isSet(object.settledDenom) ? String(object.settledDenom) : "",
      startTime: isSet(object.startTime) ? fromJsonTimestamp(object.startTime) : undefined,
      preLaunchTime: isSet(object.preLaunchTime) ? fromJsonTimestamp(object.preLaunchTime) : undefined,
      soldAmt: isSet(object.soldAmt) ? String(object.soldAmt) : "",
      claimedAmt: isSet(object.claimedAmt) ? String(object.claimedAmt) : "",
      incentivePlanParams: isSet(object.incentivePlanParams)
        ? IncentivePlanParams.fromJSON(object.incentivePlanParams)
        : undefined,
    };
  },

  toJSON(message: Plan): unknown {
    const obj: any = {};
    message.id !== undefined && (obj.id = Math.round(message.id));
    message.rollappId !== undefined && (obj.rollappId = message.rollappId);
    message.moduleAccAddress !== undefined && (obj.moduleAccAddress = message.moduleAccAddress);
    message.totalAllocation !== undefined
      && (obj.totalAllocation = message.totalAllocation ? Coin.toJSON(message.totalAllocation) : undefined);
    message.bondingCurve !== undefined
      && (obj.bondingCurve = message.bondingCurve ? BondingCurve.toJSON(message.bondingCurve) : undefined);
    message.settledDenom !== undefined && (obj.settledDenom = message.settledDenom);
    message.startTime !== undefined && (obj.startTime = message.startTime.toISOString());
    message.preLaunchTime !== undefined && (obj.preLaunchTime = message.preLaunchTime.toISOString());
    message.soldAmt !== undefined && (obj.soldAmt = message.soldAmt);
    message.claimedAmt !== undefined && (obj.claimedAmt = message.claimedAmt);
    message.incentivePlanParams !== undefined && (obj.incentivePlanParams = message.incentivePlanParams
      ? IncentivePlanParams.toJSON(message.incentivePlanParams)
      : undefined);
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<Plan>, I>>(object: I): Plan {
    const message = createBasePlan();
    message.id = object.id ?? 0;
    message.rollappId = object.rollappId ?? "";
    message.moduleAccAddress = object.moduleAccAddress ?? "";
    message.totalAllocation = (object.totalAllocation !== undefined && object.totalAllocation !== null)
      ? Coin.fromPartial(object.totalAllocation)
      : undefined;
    message.bondingCurve = (object.bondingCurve !== undefined && object.bondingCurve !== null)
      ? BondingCurve.fromPartial(object.bondingCurve)
      : undefined;
    message.settledDenom = object.settledDenom ?? "";
    message.startTime = object.startTime ?? undefined;
    message.preLaunchTime = object.preLaunchTime ?? undefined;
    message.soldAmt = object.soldAmt ?? "";
    message.claimedAmt = object.claimedAmt ?? "";
    message.incentivePlanParams = (object.incentivePlanParams !== undefined && object.incentivePlanParams !== null)
      ? IncentivePlanParams.fromPartial(object.incentivePlanParams)
      : undefined;
    return message;
  },
};

function createBaseIncentivePlanParams(): IncentivePlanParams {
  return { startTimeAfterSettlement: undefined, numEpochsPaidOver: 0 };
}

export const IncentivePlanParams = {
  encode(message: IncentivePlanParams, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.startTimeAfterSettlement !== undefined) {
      Duration.encode(message.startTimeAfterSettlement, writer.uint32(10).fork()).ldelim();
    }
    if (message.numEpochsPaidOver !== 0) {
      writer.uint32(16).uint64(message.numEpochsPaidOver);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): IncentivePlanParams {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseIncentivePlanParams();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.startTimeAfterSettlement = Duration.decode(reader, reader.uint32());
          break;
        case 2:
          message.numEpochsPaidOver = longToNumber(reader.uint64() as Long);
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): IncentivePlanParams {
    return {
      startTimeAfterSettlement: isSet(object.startTimeAfterSettlement)
        ? Duration.fromJSON(object.startTimeAfterSettlement)
        : undefined,
      numEpochsPaidOver: isSet(object.numEpochsPaidOver) ? Number(object.numEpochsPaidOver) : 0,
    };
  },

  toJSON(message: IncentivePlanParams): unknown {
    const obj: any = {};
    message.startTimeAfterSettlement !== undefined && (obj.startTimeAfterSettlement = message.startTimeAfterSettlement
      ? Duration.toJSON(message.startTimeAfterSettlement)
      : undefined);
    message.numEpochsPaidOver !== undefined && (obj.numEpochsPaidOver = Math.round(message.numEpochsPaidOver));
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<IncentivePlanParams>, I>>(object: I): IncentivePlanParams {
    const message = createBaseIncentivePlanParams();
    message.startTimeAfterSettlement =
      (object.startTimeAfterSettlement !== undefined && object.startTimeAfterSettlement !== null)
        ? Duration.fromPartial(object.startTimeAfterSettlement as Duration)
        : undefined;
    message.numEpochsPaidOver = object.numEpochsPaidOver ?? 0;
    return message;
  },
};

declare var self: any | undefined;
declare var window: any | undefined;
declare var global: any | undefined;
var globalThis: any = (() => {
  if (typeof globalThis !== "undefined") {
    return globalThis;
  }
  if (typeof self !== "undefined") {
    return self;
  }
  if (typeof window !== "undefined") {
    return window;
  }
  if (typeof global !== "undefined") {
    return global;
  }
  throw "Unable to locate global object";
})();

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends Array<infer U> ? Array<DeepPartial<U>> : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function toTimestamp(date: Date): Timestamp {
  const seconds = date.getTime() / 1_000;
  const nanos = (date.getTime() % 1_000) * 1_000_000;
  return { seconds: Long.fromNumber(seconds), nanos };
}

function fromTimestamp(t: Timestamp): Date {
  let millis = Number(t.seconds) * 1_000;
  millis += t.nanos / 1_000_000;
  return new Date(millis);
}

function fromJsonTimestamp(o: any): Date {
  if (o instanceof Date) {
    return o;
  } else if (typeof o === "string") {
    return new Date(o);
  } else {
    return fromTimestamp(Timestamp.fromJSON(o));
  }
}

function longToNumber(long: Long): number {
  if (long.gt(Number.MAX_SAFE_INTEGER)) {
    throw new globalThis.Error("Value is larger than Number.MAX_SAFE_INTEGER");
  }
  return long.toNumber();
}

if (_m0.util.Long !== Long) {
  _m0.util.Long = Long as any;
  _m0.configure();
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
